import React, { useEffect, useState } from 'react';
import { LIVING_MODELS } from '../../../lib/constants';
import { Badge } from '../../ui/Badge';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { X, UserPlus, Users, Sparkle } from '@phosphor-icons/react';
import { OnboardingDraft } from './types';
import { cn } from '../../../lib/utils';
interface StepLivingModelProps {
  data: Partial<OnboardingDraft>;
  updateData: (data: Partial<OnboardingDraft>) => void;
  onAutoAdvance?: () => void;
}
function DotsIcon({
  count,
  isSelected
}: {
  count: number;
  isSelected: boolean;
}) {
  const dotClass = `w-3 h-3 rounded-full transition-colors ${isSelected ? 'bg-lime-1' : 'bg-light-2'}`;
  if (count === 1) {
    return <div className="flex items-center justify-center h-10" data-id="element-1613">
        <span className={dotClass} data-id="element-1614" />
      </div>;
  }
  if (count === 2) {
    return <div className="flex items-center justify-center gap-1 h-10" data-id="element-1615">
        <span className={dotClass} data-id="element-1616" />
        <span className={dotClass} data-id="element-1617" />
      </div>;
  }
  // 3 dots — triangle pattern (1 top, 2 bottom)
  return <div className="flex flex-col items-center justify-center h-10 gap-0.5" data-id="element-1618">
      <span className={dotClass} data-id="element-1619" />
      <div className="flex gap-1" data-id="element-1620">
        <span className={dotClass} data-id="element-1621" />
        <span className={dotClass} data-id="element-1622" />
      </div>
    </div>;
}
function ModelIcon({
  icon,
  isSelected
}: {
  icon: string;
  isSelected: boolean;
}) {
  if (icon === 'dots-1') return <DotsIcon count={1} isSelected={isSelected} data-id="element-1623" />;
  if (icon === 'dots-2') return <DotsIcon count={2} isSelected={isSelected} data-id="element-1624" />;
  if (icon === 'dots-3') return <DotsIcon count={3} isSelected={isSelected} data-id="element-1625" />;
  if (icon === 'sparkle') {
    return <div className="flex items-center justify-center h-10" data-id="element-1626">
        <Sparkle className="w-7 h-7 text-lime-1" weight="fill" data-id="element-1627" />
      </div>;
  }
  return null;
}
export function StepLivingModel({
  data,
  updateData,
  onAutoAdvance
}: StepLivingModelProps) {
  const [inviteHandle, setInviteHandle] = useState('');
  // Matchmaking ("Caut colegi") is deferred for MVP. If a saved draft has
  // it selected, coerce back to 'invite' on mount.
  useEffect(() => {
    if (data.livingModel === 'combo' && data.comboMode === 'matchmaking') {
      updateData({
        comboMode: 'invite'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleModelSelect = (model: string) => {
    updateData({
      livingModel: model as any,
      comboMode: model === 'combo' ? 'invite' : null
    });
    if (model !== 'combo' && onAutoAdvance) {
      setTimeout(() => onAutoAdvance(), 250);
    }
  };
  const handleAddMember = () => {
    if (!inviteHandle.trim()) return;
    const newMember = {
      id: Math.random().toString(36).substring(7),
      name: inviteHandle,
      avatar: `https://i.pravatar.cc/150?u=${inviteHandle}`
    };
    updateData({
      comboMembers: [...(data.comboMembers || []), newMember]
    });
    setInviteHandle('');
  };
  const handleRemoveMember = (id: string) => {
    updateData({
      comboMembers: (data.comboMembers || []).filter(m => m.id !== id)
    });
  };
  return <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1628">
      <div data-id="element-1629">
        <h2 className="text-2xl font-bold text-light-1" data-id="element-1630">Cu cine te muți?</h2>
        <p className="text-light-3 mt-2" data-id="element-1631">
          Asta ne ajută să-ți construim profilul perfect.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1632">
        {LIVING_MODELS.map(model => {
        const isSelected = data.livingModel === model.value;
        return <button key={model.value} onClick={() => handleModelSelect(model.value)} className={`relative flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all duration-200 ${isSelected ? 'border-lime-1 bg-lime-5' : 'border-dark-3 bg-dark-2 hover:border-dark-4 hover:bg-dark-3'}`} data-id="element-1633">
              {model.isNew && <div className="absolute top-3 right-3" data-id="element-1634">
                  <Badge variant="info" className="bg-lime-1 text-dark-1" data-id="element-1635">
                    Nou
                  </Badge>
                </div>}
              <div className="mb-3" data-id="element-1636">
                <ModelIcon icon={model.icon} isSelected={isSelected} data-id="element-1637" />
              </div>
              <span className="font-semibold text-light-1" data-id="element-1638">{model.label}</span>
              {model.subtitle && <span className="text-xs text-light-3 mt-1" data-id="element-1639">
                  {model.subtitle}
                </span>}
            </button>;
      })}
      </div>

      {data.livingModel === 'combo' && <div className="mt-8 p-6 rounded-2xl bg-dark-2 border border-dark-3 space-y-6 animate-in fade-in slide-in-from-top-4" data-id="element-1640">
          {/* Tabbed control — "Caut colegi" is disabled for MVP */}
          <div className="flex gap-2 p-1 bg-dark-1 rounded-lg" data-id="element-1641">
            <button type="button" onClick={() => updateData({
          comboMode: 'invite'
        })} className={cn('flex-1 py-2 text-sm font-medium rounded-md transition-colors', data.comboMode === 'invite' ? 'bg-dark-3 text-light-1' : 'text-light-3 hover:text-light-2')} data-id="element-1642">
              Am deja gașca
            </button>
            <button type="button" disabled aria-disabled="true" title="În curând — momentan poți forma grupul doar cu persoane pe care le cunoști" className="flex-1 py-2 text-sm font-medium rounded-md text-light-3 opacity-50 cursor-not-allowed inline-flex items-center justify-center gap-2" data-id="element-1643">
              <span data-id="element-1644">Caut colegi</span>
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-dark-3 text-light-3 border border-dark-4" data-id="element-1645">
                În curând
              </span>
            </button>
          </div>

          {/* Invite-only body */}
          <div className="space-y-4" data-id="element-1646">
            <div className="flex gap-2" data-id="element-1647">
              <Input placeholder="Caută handle (ex: @olivia.p)" value={inviteHandle} onChange={e => setInviteHandle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddMember()} className="bg-dark-1 border-dark-3" data-id="element-1648" />
              <Button onClick={handleAddMember} variant="outline" className="border-dark-3 hover:bg-dark-3 shrink-0" data-id="element-1649">
                <UserPlus className="w-4 h-4" data-id="element-1650" />
              </Button>
            </div>

            <div className="relative" data-id="element-1651">
              <div className="absolute inset-0 flex items-center" data-id="element-1652">
                <span className="w-full border-t border-dark-3" data-id="element-1653" />
              </div>
              <div className="relative flex justify-center text-xs uppercase" data-id="element-1654">
                <span className="bg-dark-2 px-2 text-light-3" data-id="element-1655">SAU</span>
              </div>
            </div>

            <Button variant="outline" className="w-full border-lime-4 text-lime-1 hover:bg-lime-5" data-id="element-1656">
              Generează link de invite WhatsApp
            </Button>

            {data.comboMembers && data.comboMembers.length > 0 && <div className="pt-4 space-y-3" data-id="element-1657">
                <p className="text-sm font-medium text-light-2" data-id="element-1658">
                  Membri adăugați:
                </p>
                {data.comboMembers.map(member => <div key={member.id} className="flex items-center justify-between p-3 rounded-xl bg-dark-1 border border-dark-3" data-id="element-1659">
                    <div className="flex items-center gap-3" data-id="element-1660">
                      <img src={member.avatar} alt={member.name} className="w-8 h-8 rounded-full" data-id="element-1661" />
                      <span className="text-sm font-medium text-light-1" data-id="element-1662">
                        {member.name}
                      </span>
                    </div>
                    <button onClick={() => handleRemoveMember(member.id)} className="p-1 text-light-3 hover:text-red-1 transition-colors" data-id="element-1663">
                      <X className="w-4 h-4" data-id="element-1664" />
                    </button>
                  </div>)}
                <div className="p-4 mt-4 rounded-xl bg-lime-5 border border-lime-4 flex items-center gap-3" data-id="element-1665">
                  <div className="w-10 h-10 rounded-full bg-lime-1 flex items-center justify-center text-dark-1 shrink-0" data-id="element-1666">
                    <Users weight="fill" data-id="element-1667" />
                  </div>
                  <div data-id="element-1668">
                    <p className="text-sm font-semibold text-light-1" data-id="element-1669">
                      Super-Card generat
                    </p>
                    <p className="text-xs text-light-3" data-id="element-1670">
                      Tu și {data.comboMembers.length} prieteni
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
}